<template>
    <VJoyDropdown
        class="edit-experience-portfolio-items-select"
        :append-to="appendToElement"
        :close-on-select="false"
        :data-testid="dataTestid"
        direction="up"
        height="40vh"
        @dropdown:hide="isOpen = false"
        @dropdown:open="isOpen = true"
    >
        <template #dropdown-button>
            <!-- eslint-disable-next-line @maltjoy/eslint-rules/no-html-button -->
            <button
                type="button"
                class="edit-experience-portfolio-items-select__trigger"
                :class="{'edit-experience-portfolio-items-select__trigger--placeholder': !selectedLabel}"
                :aria-expanded="isOpen"
                :title="selectedLabel || undefined"
            >
                <span class="edit-experience-portfolio-items-select__value">{{ selectedLabel || placeholder }}</span>
                <VJoyIcon class="edit-experience-portfolio-items-select__chevron" name="chevron-down" size="xsmall" />
            </button>
        </template>
        <template #dropdown-items>
            <p v-if="portfolioItems.length === 0" class="edit-experience-portfolio-items-select__empty">
                {{ t('profile.new.experience.portfolio.items.empty') }}
            </p>
            <VJoyCheckboxGroup v-else>
                <VJoyCheckbox
                    v-for="item in portfolioItems"
                    :id="`${dataTestid}-option-${item.id}`"
                    :key="item.id"
                    :checked="model.includes(item.id)"
                    :data-testid="`${dataTestid}-option-${item.id}`"
                    :disabled="isItemDisabled(item)"
                    :value="item.id"
                    @update:checked="(checked: boolean) => toggleItem(item.id, checked)"
                >
                    <span class="edit-experience-portfolio-items-select__option">
                        <span>{{ portfolioItemTitle(item.title) }}</span>
                        <span v-if="item.unavailableReason" class="edit-experience-portfolio-items-select__reason">
                            {{ item.unavailableReason }}
                        </span>
                    </span>
                </VJoyCheckbox>
            </VJoyCheckboxGroup>
        </template>
    </VJoyDropdown>
</template>

<script lang="ts" setup>
    import {computed, ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {VJoyCheckbox, VJoyCheckboxGroup, VJoyDropdown, VJoyIcon} from '@maltjoy/core-vue';
    import type {PortfolioItemSummaryResource} from '@api';
    import {usePortfolioItemsStore} from '~/features/portfolio/section/store/portfolio-items.store';

    interface Props {
        appendToElement?: string;
        dataTestid?: string;
        experienceId?: string;
    }
    const {appendToElement = '.profile-id-or-alias', dataTestid = 'edit-experience-portfolio-items-select', experienceId} = defineProps<Props>();
    const model = defineModel<string[]>({default: () => []});

    const {t} = useTranslation();
    const isOpen = ref(false);
    const {portfolioItems: availablePortfolioItems} = storeToRefs(usePortfolioItemsStore());
    const placeholder = computed(() => t('profile.new.experience.portfolio.items.placeholder'));

    function portfolioItemTitle(title?: string | null): string {
        return title || t('profile.section.portfolio.item.untitled');
    }

    type PortfolioItemOption = PortfolioItemSummaryResource & {
        unavailableReason?: string;
    };

    function unavailableReason(item: PortfolioItemSummaryResource): string | undefined {
        const reasons = [
            !item.published ? t('profile.new.experience.portfolio.items.in.draft') : undefined,
            item.experienceId && item.experienceId !== experienceId ? t('profile.new.experience.portfolio.items.already.linked') : undefined,
        ].filter((reason): reason is string => !!reason);

        return reasons.length > 0 ? reasons.join(' · ') : undefined;
    }

    const portfolioItems = computed(() =>
        availablePortfolioItems.value.map((item): PortfolioItemOption => ({
            ...item,
            unavailableReason: unavailableReason(item),
        })),
    );

    const selectedTitles = computed(() =>
        model.value.flatMap((id) => {
            const item = availablePortfolioItems.value.find((portfolioItem) => portfolioItem.id === id);
            return item ? [portfolioItemTitle(item.title)] : [];
        }),
    );

    const selectedLabel = computed(() => {
        if (selectedTitles.value.length >= 3) {
            return t('profile.new.experience.portfolio.items.selected', {count: selectedTitles.value.length});
        }
        return selectedTitles.value.join(', ');
    });

    function isItemDisabled(item: PortfolioItemOption): boolean {
        return !!item.unavailableReason && !model.value.includes(item.id);
    }

    function toggleItem(itemId: string, checked: boolean) {
        if (checked) {
            model.value = model.value.includes(itemId) ? model.value : [...model.value, itemId];
            return;
        }
        model.value = model.value.filter((id) => id !== itemId);
    }
</script>
<style lang="scss" scoped>
    .edit-experience-portfolio-items-select {
        &.joy-dropdown {
            display: block;
            width: 100%;
            min-width: 0;
            contain: inline-size;

            :deep(.joy-dropdown__button) {
                display: block;
                width: 100%;
            }
        }

        // Joy writes an inline width in px, which ignores a percentage prop.
        :deep(.joy-dropdown__list--left) {
            right: 0;
            left: 0;
        }

        :deep(.joy-dropdown-list) {
            box-sizing: border-box;
            width: 100% !important;
            max-width: 100% !important;
        }

        &__trigger {
            all: unset;
            box-sizing: border-box;
            position: relative;
            display: block;
            width: 100%;
            overflow: hidden;
            height: var(--joy-form-field-height-medium);
            padding: 0 var(--joy-core-spacing-10) 0 var(--joy-core-spacing-5);
            border: var(--joy-form-field-border-width) solid var(--trigger-border-color);
            border-radius: var(--joy-form-field-radius-medium);
            background: var(--trigger-background);
            color: var(--trigger-color);
            font-family: var(--joy-font-family-base);
            font-size: var(--joy-font-size-primary-400);
            line-height: var(--joy-line-height-large);
            cursor: pointer;
            transition: all var(--joy-transition-duration-short) var(--joy-transition-timing-function);

            --trigger-color: var(--joy-color-text-default);
            --trigger-background: var(--joy-color-background-default);
            --trigger-icon-color: var(--joy-color-neutral-40);
            --trigger-border-color: var(--joy-color-border-default);

            &--placeholder {
                --trigger-color: var(--joy-color-text-support);
            }

            &:hover {
                --trigger-border-color: var(--joy-color-border-hover);
            }

            &:focus {
                --trigger-border-color: var(--joy-color-border-focus);

                box-shadow: none;
            }
        }

        &__value {
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__chevron {
            position: absolute;
            right: var(--joy-core-spacing-5);
            top: var(--joy-core-spacing-4);
            color: var(--trigger-icon-color);
            pointer-events: none;
        }

        &__empty {
            margin: 0;
        }

        &__option {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-1);
            min-width: 0;
            overflow-wrap: anywhere;
        }

        &__reason {
            color: var(--joy-color-neutral-40);
            font-size: var(--joy-font-size-primary-200);
        }
    }
</style>
